import createItemsCache from "../createItemsCache"
import {FilterCookie} from "../../models/cookie"
import {SearchState} from "../../models/SearchState"
import {Filters} from "../../models/Filter"
import {isPriceFilter} from "../../models/Filter/typeGuards"
import {SearchApiResponse} from "../../models/searchApi"
import {SearchApiRequestTypes} from "../../config/constants"
import getOpenFiltersAndViewMoreFromExistingFilters from "../getOpenFiltersAndViewMoreFromExistingFilters"

interface ESIReplacedSearchApiResponse extends SearchApiResponse {
    searchBannerHtml?: null | string
    searchSource: string
}

interface mapApiDataToStateProps {
    data: ESIReplacedSearchApiResponse
    type: SearchApiRequestTypes
    isServerSideRender: boolean
    cacheStart: number
    rememberFilters: boolean
    cookie?: FilterCookie
    existingFilters?: Filters
    sliceStart: number
    sliceEnd: number
    initialLoad: boolean
    initialItemsPerPage: number
    subsequentItemsPerPage: number
}
const mapApiDataToState = ({
    data,
    type,
    isServerSideRender,
    cacheStart,
    rememberFilters,
    cookie,
    existingFilters,
    sliceStart,
    sliceEnd,
    initialLoad,
    initialItemsPerPage,
    subsequentItemsPerPage,
}: mapApiDataToStateProps): SearchState => {
    const state = {
        totalResults: data.totalResults,
        filters: data.filters,
        filtersSort: data.filtersSort,
        facets: data.facets,
        items: data.items.slice(sliceStart, sliceEnd),
        sorting: data.sorting,
        title: data.title,
        autoCorrectQuery: data.autoCorrectQuery,
        relaxedQuery: data.relaxedQuery,
        includedComponents: data.includedComponents,
        templateOverride: data.templateOverride,
        searchCategory: data.searchCategory,
        searchBannerHtml: data.searchBannerHtml ?? null,
        searchBannerJson: data.searchBannerJson ?? null,
        searchProviderRequestUrl: data.searchProviderRequestUrl ?? "",
        itemsCache: {},
        searchSource: data.searchSource,
    }

    state.itemsCache = createItemsCache({
        items: data.items,
        startFrom: cacheStart,
        initialLoad,
        initialItemsPerPage,
        subsequentItemsPerPage,
    })

    if (state?.sorting?.options) {
        const sortOptions = state.sorting

        // if a user navigates to a page with an invalid search value
        // e.g. search?isort=foo or /shop.../isort-bar
        // the API will default to sorting by score -- we need to reflect that in the UI
        const availableOptions = sortOptions.options.map(opt => opt.value)
        const isValidSortOption = availableOptions.includes(sortOptions.selected)

        if (!isValidSortOption) {
            // default to score (most relevant) or fall back if not available
            sortOptions.selected = availableOptions.includes("score") ? "score" : availableOptions[0]
        }
    }

    if (!rememberFilters) {
        if (!isServerSideRender) {
            state.filters = getOpenFiltersAndViewMoreFromExistingFilters(existingFilters || {}, data.filters)
            return state as SearchState
        }
    }

    if (!cookie) {
        return state as SearchState
    }

    // Due to caching on Category pages, we can't apply this logic on the SSR for category
    if (!isServerSideRender || type === SearchApiRequestTypes.Keyword) {
        Object.entries(cookie.filterCategorySettings).forEach(([key, value]) => {
            if (key in state.filters) {
                const filter = state.filters[key]
                if (isPriceFilter(filter)) {
                    filter.isFilterOpen = value.isOpen
                } else {
                    filter.isFilterOpen = value.isOpen
                    filter.isViewMoreOpen = value.viewMoreOpened
                }
            }
        })
    }
    return state as SearchState
}

export default mapApiDataToState
